import { useMemo } from 'react';
import { BagTile } from './HexBoard';
import type { Tile } from '../types';

// ─────────────────────────────────────────────────────────────────────
// Bag (interactive)
// ─────────────────────────────────────────────────────────────────────
interface BagProps {
  tiles: Tile[];
  selectedId: string | null;
  onSelectTile: (id: string) => void;
  onTilePointerDown?: (tile: Tile, e: React.PointerEvent) => void;
  draggingTileId?: string | null;
  highlightedIds?: Set<string>;
  /// Bag tile being suggested by an active hint. Pulses with the
  /// accent color and is the source endpoint of the hint connector.
  hintTileId?: string | null;
  isReturnTarget?: boolean;
  tileSize?: number;
}

export function Bag({ tiles, selectedId, onSelectTile, onTilePointerDown, draggingTileId, highlightedIds, hintTileId = null, isReturnTarget = false, tileSize = 44 }: BagProps) {
  const remaining = tiles.filter(t => !t.placed).length;
  const displayTiles = useMemo(() => {
    return tiles.slice().sort((a, b) => {
      if (a.value !== b.value) return a.value - b.value;
      return a.id.localeCompare(b.id);
    });
  }, [tiles]);

  return (
    <div>
      <div className="hm-bag-header">
        <span>Bag</span>
        <span className="hm-bag-count">
          {remaining} of {tiles.length} remaining
        </span>
      </div>
      {/* When a focus or pin highlight is active, the highlight system
          DIMS the invalid tiles instead of decorating the valid ones —
          less visual noise and the eye lands on the legal candidates
          naturally. `focusActive` here doubles as "is the highlight
          system on at all?" so we only dim mid-focus, never resting. */}
      <div className={`hm-bag ${isReturnTarget ? 'return-target' : ''}`} data-bag-drop role="list" aria-label="Tile bag">
        {(() => {
          const focusActive = !!highlightedIds && highlightedIds.size > 0;
          return displayTiles.map(t => {
            const isSelected = t.id === selectedId;
            const isDragging = draggingTileId === t.id;
            const isHighlighted = focusActive && highlightedIds!.has(t.id);
            const isDimmed = focusActive && !isHighlighted && !t.placed;
            const isHinted = hintTileId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-tile-id={t.id}
                className={`hm-bag-tile ${t.placed ? 'placed' : ''} ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isDimmed ? 'dimmed' : ''} ${isHinted ? 'hinted' : ''}`}
                disabled={!!t.placed}
                onClick={() => onSelectTile(t.id)}
                onPointerDown={(e) => onTilePointerDown && onTilePointerDown(t, e)}
                aria-pressed={isSelected}
                aria-label={`Tile value ${t.value}${t.placed ? ' (placed)' : ''}${isDimmed ? ' (not a candidate)' : ''}`}
              >
                {/* `dim` here is the *placed* state (greyed permanently
                    while on the board). The "invalid candidate" dim is a
                    softer opacity treatment applied at the button level
                    via `.hm-bag-tile.dimmed` CSS — separate intensity
                    from the placed look. */}
                <BagTile value={t.value} dim={!!t.placed} selected={isSelected} size={tileSize} />
              </button>
            );
          });
        })()}
      </div>
    </div>
  );
}
